import { useSimpleReducer } from "@common/hooks";
import React, { createContext, PropsWithChildren, useEffect } from "react";
import {
  canEditTicket as canEditTicketFunc,
  canProduceMeeting as canProduceMeetingFunc,
  canViewElogsAndRevisions,
  canViewTicket as canViewTicketFunc,
} from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  ElogDetail,
  ElogDevice,
  MeetingElog,
  MeetingInstance,
} from "@services/meetings/types";
import { User } from "@services/user/types";
import { getProducers } from "@services/user";
import { getElogProperties } from "@services/settings";
import { getMeetingInfrastructure } from "@services/meetings";
import { useErrorNotification } from "@common/components/Notification";

export interface IElogMeetingContext {
  dispatchElogMeeting: (changes: Partial<MeetingElog>) => void;
  dispatchElogMeetingDetails: (changes: Partial<ElogDetail>) => void;
  form: {
    elogMeeting: Partial<MeetingElog>;
    details: Partial<ElogDetail>;
  };

  canEditTicket: boolean;
  canProduceMeeting: boolean;
  canViewElog: boolean;
  canViewTicket: boolean;
  producers: User[];
  mcus: ElogDevice[];
  enableAttendanceTracking: boolean;
  showBillingTab: boolean;
}

export interface IFieldValueContext {
  producers: User[];
  mcus: ElogDevice[];
  enableAttendanceTracking: boolean;
  showBillingTab: boolean;
}

const defaultContext: IElogMeetingContext = {
  dispatchElogMeeting: () => {},
  dispatchElogMeetingDetails: () => {},
  form: { elogMeeting: {}, details: {} },
  canEditTicket: false,
  canProduceMeeting: false,
  canViewElog: false,
  canViewTicket: false,
  producers: [],
  mcus: [],
  enableAttendanceTracking: false,
  showBillingTab: false,
};

export const FieldValueContext = createContext<IFieldValueContext>({
  producers: [],
  mcus: [],
  enableAttendanceTracking: false,
  showBillingTab: false,
});

export const ElogMeetingContext =
  createContext<IElogMeetingContext>(defaultContext);

export type ElogMeetingForm = Partial<MeetingElog>;

export const ElogMeetingContextProvider = (
  props: PropsWithChildren<{
    elogMeeting?: MeetingElog;
    meeting?: MeetingInstance;
  }>
) => {
  const [user] = useAuthenticatedUser();
  const dispatchError = useErrorNotification();
  const [elogMeeting, dispatchElogMeeting] = useSimpleReducer<
    Partial<MeetingElog>
  >({});
  const [details, dispatchElogMeetingDetails] = useSimpleReducer<
    Partial<ElogDetail>
  >({});
  const [state, dispatch] = useSimpleReducer<IFieldValueContext>({
    producers: [],
    mcus: [],
    enableAttendanceTracking: false,
    showBillingTab: false,
  });
  const canEditTicket = canEditTicketFunc(user);
  const canProduceMeeting = canProduceMeetingFunc(user);
  const canViewElog = canViewElogsAndRevisions(user);
  const canViewTicket = canViewTicketFunc(user);
  const fetchProducers = async (accountId: string) => {
    try {
      if (canViewTicket) {
        let producers = await getProducers(accountId);
        if (canProduceMeeting) {
          if (!producers.find((producer) => producer.email == user?.email)) {
            producers.push(user as User);
          }
        }

        producers.sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`
          )
        );
        dispatch({
          producers: producers,
        });
      }
    } catch (e) {
      dispatchError(e);
    }
  };
  const fetchSettings = async (accountId: string) => {
    try {
      let settings = await getElogProperties("account", accountId);
      dispatch({
        enableAttendanceTracking:
          settings?.enableAttendanceTracking.value === true,
        showBillingTab: settings?.showBillingTab.value === true,
      });
    } catch (e) {
      dispatchError(e);
    }
  };
  const fetchMCUs = async (accountId: string) => {
    try {
      dispatch({
        mcus: (await getMeetingInfrastructure(accountId)).mcu,
      });
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    if (props.meeting?.account?.id) {
      fetchProducers(props.meeting?.account?.id);
      fetchSettings(props.meeting?.account?.id);
      fetchMCUs(props.meeting?.account?.id);
    }
  }, [props.meeting?.account?.id]);
  return (
    <ElogMeetingContext.Provider
      value={{
        ...defaultContext,
        dispatchElogMeeting,
        dispatchElogMeetingDetails,
        form: {
          elogMeeting: { ...props.elogMeeting, ...elogMeeting },
          details: { ...props.elogMeeting?.elogDetail, ...details },
        },
        canEditTicket,
        canProduceMeeting,
        canViewElog,
        canViewTicket,
        mcus: state.mcus,
        enableAttendanceTracking: state.enableAttendanceTracking,
        producers: state.producers,
        showBillingTab: state.showBillingTab,
      }}
    >
      {props.children}
    </ElogMeetingContext.Provider>
  );
};
